import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Briefcase, MapPin, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /workforce/shifts — Open shifts board (Blueprint Decision #5).
 *
 * Visible to verified clinicians. Each row is a staffing-gap referral
 * that the discharge engine could not place automatically — broadened
 * to the full state pool here. First clinician to claim wins.
 */
export default function WorkforceShifts() {
  const { user, getAuthHeader } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterState, setFilterState] = useState('');
  const [claiming, setClaiming] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterState ? `?state=${filterState.toUpperCase()}` : '';
      const { data } = await axios.get(`${API}/workforce/shifts${params}`, getAuthHeader());
      setShifts(data.shifts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load shifts');
    } finally {
      setLoading(false);
    }
  }, [filterState, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const claim = async (shiftId) => {
    setClaiming((s) => ({ ...s, [shiftId]: true }));
    try {
      await axios.post(`${API}/workforce/shifts/${shiftId}/claim`, {}, getAuthHeader());
      toast.success('Shift claimed — placement confirmed.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim shift');
    } finally {
      setClaiming((s) => ({ ...s, [shiftId]: false }));
    }
  };

  return (
    <DashboardShell data-testid="workforce-shifts-page">
      <header className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
          <Briefcase className="w-3 h-3" /> Workforce
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Open shifts</h1>
        <p className="text-sm text-slate-600 mt-1">
          Discharge referrals the placement engine couldn't auto-match. Verified clinicians in-state can claim them directly — first claim wins.
        </p>

        <div className="mt-4 flex gap-2 items-center" data-testid="shifts-state-filter">
          <input
            type="text"
            placeholder="Filter by state (e.g. TX)"
            value={filterState}
            onChange={(e) => setFilterState(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-cn-primary w-48"
            data-testid="shifts-state-input"
          />
          {filterState && (
            <button
              onClick={() => setFilterState('')}
              className="text-xs text-slate-500 hover:text-slate-700"
              data-testid="shifts-state-clear"
            >
              Clear
            </button>
          )}
        </div>
      </header>

      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-slate-500">Loading open shifts…</div>
      ) : shifts.length === 0 ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-center" data-testid="shifts-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <p className="text-slate-700 font-semibold">No open shifts right now.</p>
          <p className="text-slate-500 text-sm mt-1">All placements caught up — check back later.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="shifts-list">
          <div className="text-xs text-slate-500">{shifts.length} open shifts</div>
          {shifts.map((s) => (
            <ShiftRow
              key={s.id}
              shift={s}
              claiming={!!claiming[s.id]}
              onClaim={() => claim(s.id)}
              userVerified={user?.verification_status === 'verified' || user?.role === 'clinician'}
            />
          ))}
        </div>
      )}
    </DashboardShell>
  );
}

function ShiftRow({ shift, claiming, onClaim, userVerified }) {
  const expiresAt = shift.expires_at ? new Date(shift.expires_at) : null;
  const hoursLeft = expiresAt ? Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 3.6e6)) : null;
  return (
    <div
      className="rounded-2xl bg-white border border-slate-100 p-5 flex items-start justify-between gap-4 hover:border-cn-primary/30 transition"
      data-testid={`shift-row-${shift.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-slate-900 capitalize">
            {(shift.discipline || 'home health').replace(/_/g, ' ')}
          </span>
          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
            staffing gap
          </span>
        </div>
        <p className="text-sm text-slate-700">{shift.primary_diagnosis || 'No diagnosis on file'}</p>
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
          {shift.state && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {shift.city ? `${shift.city}, ` : ''}{shift.state}
            </span>
          )}
          {shift.estimated_minutes && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> ~{shift.estimated_minutes} min
            </span>
          )}
          {hoursLeft != null && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <AlertCircle className="w-3 h-3" /> expires in ~{hoursLeft}h
            </span>
          )}
        </div>
      </div>
      <Button
        onClick={onClaim}
        disabled={claiming || !userVerified}
        data-testid={`shift-claim-${shift.id}`}
        className="bg-cn-primary hover:bg-cn-primary-hover text-white"
      >
        {claiming ? 'Claiming…' : 'Claim shift'}
      </Button>
    </div>
  );
}
